// Section wiring: maps each UI section to an endpoint and optional render /
// autofill / post-render hooks, and drives the request/response cycle.

import { renderChallengeBreakdown, renderTokenBreakdown } from "./breakdown";
import { isRecord, strField } from "./guards";
import type { Params, SectionConfig } from "./types";

/** A form control that carries a value in this UI. */
type ValueEl = HTMLInputElement | HTMLTextAreaElement;

/** Abort in-flight requests that take longer than this (ms). */
const REQUEST_TIMEOUT_MS = 15_000;

export const sectionEl = (id: string): HTMLElement | null => document.getElementById(id);

/**
 * Render a response with one field shown separately as an HTTP header line on
 * top, followed by the remaining fields as JSON.
 */
function headerRender(headerName: string, field: string): (b: unknown) => string {
  return (b) => {
    if (!isRecord(b)) return String(b);
    const rest: Record<string, unknown> = { ...b };
    delete rest[field];
    const value = strField(b, field) ?? "";
    return `${headerName}: ${value}\n\n${JSON.stringify(rest, null, 2)}`;
  };
}

/** Assign a section field, but only when a truthy value is provided. */
export function set(id: string, param: string, value: string | undefined): void {
  const el = sectionEl(id)?.querySelector<ValueEl>(`[data-p="${param}"]`);
  if (el && value) el.value = value;
}

/**
 * Like set(), but always assigns -- including clearing the field when the value
 * is absent (e.g. a new challenge without an extension set).
 */
function setValue(id: string, param: string, value: string | undefined): void {
  const el = sectionEl(id)?.querySelector<ValueEl>(`[data-p="${param}"]`);
  if (el) el.value = value ?? "";
}

function params(id: string): Params {
  const p: Params = {};
  const section = sectionEl(id);
  if (!section) return p;
  for (const el of section.querySelectorAll<ValueEl>("[data-p]")) {
    const name = el.getAttribute("data-p");
    if (name) p[name] = el.value;
  }
  return p;
}

// Map each section id to its endpoint + (optional) success test + autofill.
export const SECTIONS: Record<string, SectionConfig> = {
  "challenge-create": {
    endpoint: "/api/challenge/create",
    render: headerRender("WWW-Authenticate", "www_authenticate"),
    fill: (b) => {
      set("challenge-inspect", "challenge", strField(b, "www_authenticate"));
      setValue("ext-validate", "extension-set", strField(b, "extension_set_b64"));
    },
  },
  "challenge-inspect": {
    endpoint: "/api/challenge/inspect",
    requires: "challenge",
    after: renderChallengeBreakdown,
  },
  "ext-build": {
    endpoint: "/api/extensions/build",
    render: (b) => strField(b, "extensions") ?? strField(b, "error") ?? "",
    fill: (b) => {
      const extensions = strField(b, "extensions");
      set("token-request", "extensions", extensions);
      set("ext-validate", "extensions", extensions);
    },
  },
  "ext-validate": {
    endpoint: "/api/extensions/validate",
    requires: "extensions",
    success: (b) => isRecord(b) && b["ok"] === true && b["valid"] === true,
  },
  "token-request": {
    endpoint: "/api/token/request",
    render: headerRender("Authorization", "authorization"),
    fill: (b) => {
      const authorization = strField(b, "authorization");
      set("token-inspect", "token", authorization);
      set("token-verify", "token", authorization);
    },
    requires: "extensions",
  },
  "token-inspect": {
    endpoint: "/api/token/inspect",
    requires: "token",
    after: renderTokenBreakdown,
  },
  "token-verify": { endpoint: "/api/token/verify", requires: "token" },
  "directory": {
    endpoint: "/.well-known/private-token-issuer-directory",
    render: (b) => {
      return JSON.stringify(b, null, 2);
    },
  },
};

export async function run(id: string): Promise<void> {
  const cfg = SECTIONS[id];
  const section = sectionEl(id);
  if (!cfg || !section) return;
  const out = section.querySelector<HTMLElement>("[data-out]");
  const btn = section.querySelector<HTMLButtonElement>("[data-run]");
  if (!out) return;

  const p = params(id);
  // Do nothing if a required input is empty.
  if (cfg.requires && !(p[cfg.requires] ?? "").trim()) return;

  if (btn) btn.disabled = true;
  out.className = "";
  out.textContent = "Working…";
  try {
    // Build the query from the raw fields so repeated names (e.g. the
    // type/info pairs in 'Build extensions') are all sent in order.
    const q = new URLSearchParams();
    for (const el of section.querySelectorAll<ValueEl>("[data-p]")) {
      const name = el.getAttribute("data-p");
      if (!name) continue;
      const val =
        el instanceof HTMLInputElement && el.type === "checkbox" ? (el.checked ? "1" : "0") : el.value;
      q.append(name, val);
    }
    const res = await fetch(`${cfg.endpoint}?${q}`, {
      method: "POST",
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    const text = await res.text();
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }

    const ok = cfg.success
      ? cfg.success(body)
      : res.ok && !(isRecord(body) && body["ok"] === false);
    out.className = ok ? "ok" : "bad";
    out.textContent = cfg.render
      ? cfg.render(body)
      : typeof body === "string"
        ? body
        : JSON.stringify(body, null, 2);
    if (ok && cfg.fill) cfg.fill(body);
    if (cfg.after) cfg.after(body, section, p, ok);
  } catch (e) {
    out.className = "bad";
    out.textContent = String(e);
    // Don't leave a stale breakdown panel next to the new error output.
    const bd = section.querySelector<HTMLElement>("[data-breakdown]");
    if (bd) bd.hidden = true;
  } finally {
    if (btn) btn.disabled = false;
  }
}

export async function getDirectory(): Promise<void> {
  const cfg = SECTIONS["directory"];
  const section = sectionEl("directory");
  if (!cfg || !section) return;
  const out = section.querySelector<HTMLElement>("[data-out]");
  if (!out) return;

  out.className = "";
  out.textContent = "Working…";
  try {
    const res = await fetch(`${cfg.endpoint}`, {
      method: "GET",
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    const body: unknown = await res.json();

    const ok = res.ok && !(isRecord(body) && body["ok"] === false);
    out.className = ok ? "ok" : "bad";
    out.textContent = cfg.render ? cfg.render(body) : JSON.stringify(body, null, 2);
  } catch (e) {
    out.className = "bad";
    out.textContent = String(e);
  }
}
